import type {
  ExpenseQueryParams,
  ExpenseStatistics,
  ExpenseSummary,
  IExpenseRepository,
  PaginatedResponse,
} from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { Expense, ExpenseCategory } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiExpenseResponse {
  id: string;
  title: string;
  amount: number;
  category: 'MAINTENANCE' | 'UTILITIES' | 'SALARIES' | 'CLEANING' | 'SECURITY' | 'INSURANCE' | 'SERVICES' | 'PAYROLL' | 'SUPPLIES' | 'REFUND' | 'OTHER';
  date: string;
  provider?: string | null;
  status: 'PAID' | 'PENDING' | 'OVERDUE' | 'VERIFYING';
  invoiceUrl?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  categoryColor?: string | null;
  sectionId?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiCreateExpenseRequest {
  title: string;
  amount: number;
  category: 'MAINTENANCE' | 'UTILITIES' | 'SALARIES' | 'CLEANING' | 'SECURITY' | 'INSURANCE' | 'SERVICES' | 'PAYROLL' | 'SUPPLIES' | 'REFUND' | 'OTHER';
  date: string;
  provider?: string;
  invoiceUrl?: string;
  categoryId?: string;
  sectionId?: string | null;
}

// Mapea la respuesta de la API al tipo Expense interno
const mapApiExpenseToExpense = (apiExpense: ApiExpenseResponse): Expense => ({
  id: apiExpense.id,
  title: apiExpense.title,
  amount: apiExpense.amount,
  category: apiExpense.category as ExpenseCategory,
  date: apiExpense.date,
  provider: apiExpense.provider || undefined,
  status: apiExpense.status === 'PAID' ? 'PAID' : 'PENDING',
  invoiceUrl: apiExpense.invoiceUrl || undefined,
  categoryId: apiExpense.categoryId ?? null,
  categoryName: apiExpense.categoryName ?? null,
  categoryColor: apiExpense.categoryColor ?? null,
  sectionId: apiExpense.sectionId ?? null,
});

export class ApiExpenseRepository implements IExpenseRepository {
  async getAllExpenses(): Promise<Expense[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiExpenseResponse>>('v1/expenses');
    return response.data.map(mapApiExpenseToExpense);
  }

  async getStatistics(): Promise<ExpenseStatistics> {
    const client = getApiClient();
    return await client.get<ExpenseStatistics>('v1/expenses/statistics');
  }

  async getSummary(startDate: string, endDate: string): Promise<ExpenseSummary> {
    const client = getApiClient();
    return await client.get<ExpenseSummary>(
      `v1/expenses/summary?${buildQueryString({ startDate, endDate })}`
    );
  }

  async getExpenses(params?: ExpenseQueryParams): Promise<PaginatedResponse<Expense>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiExpenseResponse>>(
      `v1/expenses?${queryString}`
    );
    return {
      ...response,
      data: response.data.map(mapApiExpenseToExpense),
    };
  }

  async createExpense(expense: Omit<Expense, 'id'>): Promise<Expense> {
    const client = getApiClient();
    const request: ApiCreateExpenseRequest = {
      title: expense.title,
      amount: expense.amount,
      category: expense.category,
      date: expense.date,
      provider: expense.provider,
      invoiceUrl: expense.invoiceUrl,
      categoryId: expense.categoryId ?? undefined,
      sectionId: expense.sectionId ?? undefined,
    };
    const apiExpense = await client.post<ApiExpenseResponse>('v1/expenses', request);
    return mapApiExpenseToExpense(apiExpense);
  }

  async deleteExpense(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/expenses/${id}`);
  }

  async getExpenseById(id: string): Promise<Expense | null> {
    const client = getApiClient();
    try {
      const apiExpense = await client.get<ApiExpenseResponse>(`v1/expenses/${id}`);
      return mapApiExpenseToExpense(apiExpense);
    } catch {
      return null;
    }
  }

  async updateExpense(expense: Expense): Promise<Expense> {
    const client = getApiClient();
    const request: Partial<ApiCreateExpenseRequest> & { status?: string } = {
      title: expense.title,
      amount: expense.amount,
      category: expense.category,
      date: expense.date,
      provider: expense.provider,
      invoiceUrl: expense.invoiceUrl,
      status: expense.status,
      sectionId: expense.sectionId,
    };
    const apiExpense = await client.put<ApiExpenseResponse>(`v1/expenses/${expense.id}`, request);
    return mapApiExpenseToExpense(apiExpense);
  }

  async getExpenseStatistics(): Promise<{
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    overdueAmount: number;
    byCategory: Record<string, number>;
  }> {
    const client = getApiClient();
    return await client.get('v1/expenses/statistics');
  }

  async getExpensesSummary(startDate: string, endDate: string): Promise<{
    totalAmount: number;
    paidAmount: number;
    pendingAmount: number;
    byCategory: Record<string, number>;
    byStatus: Record<string, number>;
  }> {
    const client = getApiClient();
    return await client.get('v1/expenses/summary', {
      params: { startDate, endDate },
    });
  }

  async getExpensesByCategory(category: string): Promise<Expense[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiExpenseResponse>>(`v1/expenses/category/${category}`);
    return response.data.map(mapApiExpenseToExpense);
  }

  async getExpensesByStatus(status: string): Promise<Expense[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiExpenseResponse>>(`v1/expenses/status/${status}`);
    return response.data.map(mapApiExpenseToExpense);
  }

  async getExpensesByDateRange(startDate: string, endDate: string): Promise<Expense[]> {
    const client = getApiClient();
    const response = await client.get<PaginatedResponse<ApiExpenseResponse>>('v1/expenses/date-range', {
      params: { startDate, endDate },
    });
    return response.data.map(mapApiExpenseToExpense);
  }
}

